import { useMemo, useState } from "react";
import { Box } from "@mui/material";

import { styled } from "@mui/material/styles";
import { roundToNearestHours, sub } from "date-fns";
import {
  ActivityIcon,
  FootprintsIcon,
  GaugeIcon,
  MoonIcon,
  WavesIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { useListSessionsSessionsGetQuery } from "api";
import useFnsLocale from "hooks/useFnsLocale";
import { useFormatDurationShort } from "hooks/useFormat";
import { isNightTime, toIsoString } from "utils/date.util";

import { HistoryGraph } from "./components/HistoryGraph";
import { HistorySettings } from "./components/HistorySettings";
import { useHistoryStore } from "./historyStore";

import { Header } from "../components/Header";

const average = (array: { value: number }[]) =>
  array.length
    ? array.reduce((acc, d) => acc + d.value, 0) / array.length
    : null;

const Wrapper = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.spacing(1.5),
  paddingTop: theme.spacing(1.5),
  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "1fr",
  },
  "@media print": {
    gridTemplateColumns: "1fr 1fr",
  },
}));

type HistoryProps = {
  subjectId: string;
};

export function History({ subjectId }: HistoryProps) {
  const { t } = useTranslation();
  const { fnsLocale } = useFnsLocale();
  const formatDuration = useFormatDurationShort();

  const backCount = useHistoryStore((s) => s.backCount);
  const interval = useHistoryStore((s) => s.intervalSize);
  const timeInBed = useHistoryStore((s) => s.timeInBed);
  const timeAsleep = useHistoryStore((s) => s.timeAsleep);
  const onlyNights = useHistoryStore((s) => s.onlyNights);

  const [now] = useState<Date>(
    roundToNearestHours(new Date(), { roundingMethod: "ceil" }),
  );

  const xDomain: [Date, Date] = useMemo(() => {
    if (interval === "quarters") {
      return [
        sub(now, { days: 1, months: (backCount + 1) * 3 }),
        sub(now, { months: backCount * 3 }),
      ];
    }

    return [
      sub(now, { days: 1, [interval]: backCount + 1 }),
      sub(now, { [interval]: backCount }),
    ];
  }, [now, backCount, interval]);

  const { data: sessionData } = useListSessionsSessionsGetQuery({
    type: "vitalthings-somnofy-sm100-session",
    subjectId: subjectId,
    from: toIsoString(xDomain[0]),
    to: toIsoString(xDomain[1]),
    state: "ENDED",
    sort: "asc",
    limit: 200,
    timeInBed: JSON.stringify({ gte: timeInBed * 60 }),
    timeAsleep: JSON.stringify({ gte: timeAsleep * 60 }),
  });

  const sessions = useMemo(
    () =>
      (sessionData?.data ?? []).filter(
        (s) =>
          !onlyNights ||
          isNightTime(new Date(s.session_start)) ||
          isNightTime(new Date(s.session_end)),
      ),
    [onlyNights, sessionData?.data],
  );

  const sleepScoreData = useMemo(() => {
    const data = sessions.map((d) => ({
      datetime: new Date(d.session_end),
      value: d.sleep_score_standard,
    }));

    return {
      data: data,
      average: average(data),
      locale: fnsLocale,
      minValue: 100,
      xDomain: xDomain,
      formatter: (value: number) => Math.round(value).toString(),
    };
  }, [fnsLocale, sessions, xDomain]);

  const sleepTimeData = useMemo(() => {
    const data = sessions.map((d) => ({
      datetime: new Date(d.session_end),
      value: d.time_asleep / 3_600,
    }));

    return {
      data: data,
      average: average(data),
      locale: fnsLocale,
      minValue: 10,
      xDomain: xDomain,
      formatter: (hours: number) => formatDuration(hours * 3_600),
    };
  }, [fnsLocale, sessions, xDomain, formatDuration]);

  const movementData = useMemo(() => {
    const data = sessions.map((d) => ({
      datetime: new Date(d.session_end),
      value: d.epochs_with_movement_pct ?? 0,
    }));

    return {
      data: data,
      average: average(data),
      locale: fnsLocale,
      minValue: 100,
      xDomain: xDomain,
      formatter: (value: number) => value.toFixed(1) + "%",
    };
  }, [fnsLocale, sessions, xDomain]);

  const respirationRateData = useMemo(() => {
    const data = sessions.map((d) => ({
      datetime: new Date(d.session_end),
      value: d.rpm_non_rem_filtered_mean ?? 0,
    }));

    return {
      data: data,
      average: average(data),
      locale: fnsLocale,
      minValue: 30,
      xDomain: xDomain,
      formatter: (value: number) =>
        value.toFixed(Number.isInteger(value) ? 0 : 1) + " rpm",
    };
  }, [fnsLocale, sessions, xDomain]);

  const noPresenceData = useMemo(() => {
    const data = sessions.map((d) => ({
      datetime: new Date(d.session_end),
      value: d.number_of_times_no_presence,
    }));

    return {
      data: data,
      average: average(data),
      locale: fnsLocale,
      minValue: 6,
      xDomain: xDomain,
      formatter: (value: number) =>
        value.toFixed(Number.isInteger(value) ? 0 : 1),
    };
  }, [fnsLocale, sessions, xDomain]);

  return (
    <Box
      sx={{
        "@media print": {
          width: "100%",
        },
      }}
    >
      <Header title={t("History")} />
      <HistorySettings />
      <Wrapper>
        <HistoryGraph
          title={t("Sleep Score")}
          description={t("night_report_sleep_score_info")}
          icon={<GaugeIcon />}
          data={sleepScoreData}
        />
        <HistoryGraph
          title={t("Time asleep")}
          description={t("night_report_time_asleep_info")}
          icon={<MoonIcon />}
          data={sleepTimeData}
        />
        <HistoryGraph
          title={t("Movement")}
          description={t("night_report_movement_info")}
          icon={<WavesIcon />}
          data={movementData}
        />
        <HistoryGraph
          title={t("respiration_rate")}
          description={t("night_report_respiration_rate_info")}
          icon={<ActivityIcon />}
          data={respirationRateData}
        />
        <HistoryGraph
          title={t("No Presence")}
          description={t("night_report_out_of_bed_info")}
          data={noPresenceData}
          icon={<FootprintsIcon />}
        />
      </Wrapper>
    </Box>
  );
}
